// src/components/AreaSiteGroupSelector.tsx
import React, { useEffect, useState } from "react";
import * as Style from "../styles/AreaSiteGroupSelector.style";
import { Modal, Drawer } from 'antd';
import { MediaQuery } from 'react-responsive';

import checkSVG from '../imgs/ic_check.svg'; 

interface Group {
    group_id: number;
    group_name: string;
}
interface Site {
    site_id: number;
    site_name: string;
    groups: Group[];
}
interface Area {
    area_id: number;
    area_name: string;
    sites: Site[];
}

interface Props {
    backgroundRef: React.RefObject<HTMLDivElement | null>; 
    areas: Area[];
    selectedAreaId: number | null;
    selectedSiteId: number | null;
    selectedGroupId: number | null;
    onAreaChange: (id: number | null) => void;
    onSiteChange: (id: number | null) => void;
    onGroupChange: (id: number | null) => void;
    onGroupNameChange: (name: string | null) => void;
}


const AreaSiteGroupSelector: React.FC<Props> = ({
    backgroundRef,
    areas,
    selectedAreaId,
    selectedSiteId,
    selectedGroupId,
    onAreaChange,
    onSiteChange,
    onGroupChange,
    onGroupNameChange,
}) => {
    // modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<"area" | "site" | "group" | null>(null);
    const [localSelectedId, setLocalSelectedId] = useState<number | null>(null);
    const [localSelectedGName, setLocalSelectedGName] = useState<string | null>(''); // 그룹명

    // toast
    const [showToast, setShowToast] = useState(false);
    const [toastMsg, setToastMsg] = useState("");

    useEffect(() => {
    if (!showToast) return;
    const t = setTimeout(() => setShowToast(false), 2200);
    return () => clearTimeout(t);
    }, [showToast]);

    // helpers to find selected names (for button labels)
    const selectedArea = areas.find((a) => a.area_id === selectedAreaId) ?? null;
    const selectedSite = selectedArea?.sites.find((s) => s.site_id === selectedSiteId) ?? null;
    const selectedGroup = selectedSite?.groups.find((g) => g.group_id === selectedGroupId) ?? null;

    // open modal for mode and initialize localSelectedId from current selection
    const openModal = (mode: "area" | "site" | "group") => {
    if (mode === "site" && !selectedAreaId) {
        setToastMsg("지역을 먼저 선택하세요");
        setShowToast(true);
        return;
    }
    if (mode === "group" && !selectedSiteId) {
        setToastMsg("사이트를 먼저 선택하세요");
        setShowToast(true);
        return;
    }

    setModalMode(mode);
    setIsModalOpen(true);

    if (mode === "area") setLocalSelectedId(selectedAreaId ?? null);
    else if (mode === "site") setLocalSelectedId(selectedSiteId ?? null);
    else if (mode === "group") setLocalSelectedId(selectedGroupId ?? null);
    };

    const closeModal = () => {
    setIsModalOpen(false);
    setModalMode(null);
    setLocalSelectedId(null);
    };

    const confirmModal = () => {
    if (modalMode === "area") {
        onAreaChange(localSelectedId ?? null);
        onSiteChange(null);
        onGroupChange(null);
    } else if (modalMode === "site") {
        onSiteChange(localSelectedId ?? null);
        onGroupChange(null);
    } else if (modalMode === "group") {
        onGroupChange(localSelectedId ?? null);
        onGroupNameChange(localSelectedGName ?? '');  
    }
    closeModal();
    };

    // data list for modal depending on mode
    const modalList =
    modalMode === "area"
        ? areas
        : modalMode === "site"
        ? selectedArea?.sites ?? []
        : modalMode === "group"
        ? selectedSite?.groups ?? []
        : [];

    const modalTitle =
    modalMode === "area" ? "지역 선택" : modalMode === "site" ? "사이트 선택" : modalMode === "group" ? "그룹 선택" : "";

    return (
    <>
        {/* <MediaQuery maxWidth={767}> */}
            <Style.SelectorBoxFrame>
                {/* Area 버튼 */}
                <Style.SelectorFieldFrame
                    onClick={() => openModal("area")}
                >
                    <Style.SelectorRow>
                        <Style.SelectorFrame>
                            <Style.SelectorBadge>
                                <Style.SelectorBadgeText>
                                    지역
                                </Style.SelectorBadgeText>
                            </Style.SelectorBadge>
                            <div>
                            {selectedArea? (
                                <Style.SelectorTextSet>
                                    {selectedArea.area_name}
                                </Style.SelectorTextSet>
                            ) : (
                                <Style.SelectorText>
                                    {"지역을 선택하세요."}
                                    {/* {selectedArea ? selectedArea.area_name : "지역을 선택하세요."} */}
                                </Style.SelectorText>                        
                            )}
                            </div>
                        </Style.SelectorFrame>
                    </Style.SelectorRow>
                </Style.SelectorFieldFrame>

                <Style.SelectorSpaceFrame>
                    <Style.SelectorSpace></Style.SelectorSpace>
                </Style.SelectorSpaceFrame>

                {/* Site 버튼 */}
                <Style.SelectorFieldFrame
                    onClick={() => openModal("site")}
                >
                    <Style.SelectorRow>
                        <Style.SelectorFrame>
                            <Style.SelectorBadge>
                                <Style.SelectorBadgeText>
                                    사이트
                                </Style.SelectorBadgeText>
                            </Style.SelectorBadge>
                            <div>
                            {selectedSite? (
                                <Style.SelectorTextSet>
                                    {selectedSite.site_name} ({selectedSite.site_id})
                                </Style.SelectorTextSet>
                            ) : (
                                <Style.SelectorText>
                                    {"사이트를 선택하세요."}
                                </Style.SelectorText>                        
                            )}
                            </div>
                        </Style.SelectorFrame>
                    </Style.SelectorRow>
                </Style.SelectorFieldFrame>

                <Style.SelectorSpaceFrame>
                    <Style.SelectorSpace></Style.SelectorSpace>
                </Style.SelectorSpaceFrame>

                {/* Group 버튼 */}
                <Style.SelectorFieldFrame
                    onClick={() => openModal("group")}
                >
                    <Style.SelectorRow>
                        <Style.SelectorFrame>
                            <Style.SelectorBadge>
                                <Style.SelectorBadgeText>
                                    그룹
                                </Style.SelectorBadgeText>
                            </Style.SelectorBadge>
                            <div>
                            {selectedGroup? (
                                <Style.SelectorTextSet>
                                    {selectedGroup.group_name} ({selectedGroup.group_id})
                                </Style.SelectorTextSet>
                            ) : (
                                <Style.SelectorText>
                                    {"그룹을 선택하세요."}
                                </Style.SelectorText>                        
                            )}
                            </div>
                        </Style.SelectorFrame>
                    </Style.SelectorRow>
                </Style.SelectorFieldFrame>

            </Style.SelectorBoxFrame>


        {/* 토스트 */}
        {showToast && (
            <Style.ToastFrame>
                <Style.ToastText>{toastMsg}</Style.ToastText>
            </Style.ToastFrame>
        )}


        <MediaQuery maxWidth={767}>
        {/* 모달 (하단 시트 스타일) */}
        {isModalOpen && (
            <>
            {/* 목록 선택 영역 */}
            <Drawer
                onClose={closeModal}
                open={isModalOpen}
            >

            <Style.SheetListFrame>

                {/* 선택 타이틀 영역 */}
                <Style.SheetListTitleFrame>
                    <Style.SheetListTitleText>
                        {modalTitle}
                    </Style.SheetListTitleText>
                </Style.SheetListTitleFrame>


                {/* 목록 영역 */}
                {/* <Style.SheetListScrollFrame> */}
                    {modalList.length === 0 ? (
                        <div className="p-6 text-center text-grayscalegr-66">선택 가능한 항목이 없습니다.</div>
                    ) : (
                    <Style.SheetListScrollFrame>
                        {modalList.map((item: any) => {
                            const id = modalMode === "area" ? item.area_id : modalMode === "site" ? item.site_id : item.group_id;
                            const label = modalMode === "area" ? item.area_name : modalMode === "site" ? item.site_name : item.group_name;
                            const isSelected = id === localSelectedId;
                            return (                            
                            <Style.SheetListSelect
                                key={id}
                                onClick={() => {
                                    setLocalSelectedId(id)
                                    // group 모드일 때만 group_name을 저장
                                    if (modalMode === "group") {
                                        setLocalSelectedGName(label);
                                    }
                                }} 
                            >

                            {isSelected? (
                                <Style.SheetListSelectFocusFrame>
                                <Style.SheetListSelectTextFocus>
                                    {label}
                                </Style.SheetListSelectTextFocus>
                                <Style.SheetListSelectCheckIcon
                                    $imgUrl={checkSVG}
                                >
                                </Style.SheetListSelectCheckIcon>
                                </Style.SheetListSelectFocusFrame>
                            ) : (
                                <Style.SheetListSelectUnFocusFrame>
                                <Style.SheetListSelectTextUnFocus>
                                    {label}
                                </Style.SheetListSelectTextUnFocus>                      
                                </Style.SheetListSelectUnFocusFrame>
                            )}

                        </Style.SheetListSelect>
                    );
                    })}
                    </Style.SheetListScrollFrame>
                    )}
                {/* </Style.SheetListScrollFrame> */}

                {/* 버튼 영역 */}
                <Style.SheetListButtonFrame>
                
                    {/* <Style.SheetListButtonSubFrame> */}
                        <Style.SheetListCancelButton
                            type="button"
                            onClick={closeModal}
                        >
                            취소
                        </Style.SheetListCancelButton>

                        <Style.SheetListConfirmButton
                            type="button"
                            onClick={confirmModal}
                        >
                            선택완료
                        </Style.SheetListConfirmButton>
                    {/* </Style.SheetListButtonSubFrame> */}

                </Style.SheetListButtonFrame>


            </Style.SheetListFrame>

            </Drawer>

            </>
        )}
        </MediaQuery>
        <MediaQuery minWidth={768}>
            {/* 모달 (하단 시트 스타일) */}
            {isModalOpen && (
                <>
                {/* 목록 선택 영역 */}
            <Modal
                open={isModalOpen}
                onCancel={closeModal}
                // size={'large'}
                width={780}
                footer={null}
                styles={{
                    body:{
                        // overflowY: 'auto', // 세로 스크롤 활성화
                        maxHeight: '780px', // 👈 모달 본문의 최대 높이 설정
                    }
                }}                
            >
                <Style.SheetListFrame>

                    {/* 선택 타이틀 영역 */}
                    <Style.SheetListTitleFrame>
                        <Style.SheetListTitleText>
                            {modalTitle}
                        </Style.SheetListTitleText>
                    </Style.SheetListTitleFrame>


                    {/* 목록 영역 */}
                    {/* <Style.SheetListScrollFrame> */}
                        {modalList.length === 0 ? (
                            <div className="p-6 text-center text-grayscalegr-66">sss선택 가능한 항목이 없습니다.</div>
                        ) : (
                        <Style.SheetListScrollFrame>
                            {modalList.map((item: any) => {
                                const id = modalMode === "area" ? item.area_id : modalMode === "site" ? item.site_id : item.group_id;
                                const label = modalMode === "area" ? item.area_name : modalMode === "site" ? item.site_name : item.group_name;
                                const isSelected = id === localSelectedId;
                                return (                            
                            <Style.SheetListSelect
                                key={id}
                                onClick={() => {
                                    setLocalSelectedId(id)
                                    // group 모드일 때만 group_name을 저장
                                    if (modalMode === "group") {
                                        setLocalSelectedGName(label);
                                    }
                                }} 
                            >

                                {isSelected? (
                                    <Style.SheetListSelectFocusFrame>
                                    
                                        <Style.SheetListSelectTextFocus>
                                            {label}
                                        </Style.SheetListSelectTextFocus>
                                        <Style.SheetListSelectCheckIcon
                                            $imgUrl={checkSVG}>
                                        </Style.SheetListSelectCheckIcon>
                                     
                                     </Style.SheetListSelectFocusFrame>
                                ) : (
                                    <Style.SheetListSelectUnFocusFrame>
                                    <Style.SheetListSelectTextUnFocus>
                                        {label}
                                    </Style.SheetListSelectTextUnFocus>                      
                                    </Style.SheetListSelectUnFocusFrame>
                                )}

                            </Style.SheetListSelect>
                        );
                        })}
                        </Style.SheetListScrollFrame>
                        )}
                    {/* </Style.SheetListScrollFrame> */}

                    {/* 버튼 영역 */}
                    <Style.SheetListButtonFrame>
                    
                        {/* <Style.SheetListButtonSubFrame> */}
                            <Style.SheetListCancelButton
                                type="button"
                                onClick={closeModal}
                            >
                                취소
                            </Style.SheetListCancelButton>

                            <Style.SheetListConfirmButton
                                type="button"
                                onClick={confirmModal}
                            >
                                선택완료
                            </Style.SheetListConfirmButton>
                        {/* </Style.SheetListButtonSubFrame> */}

                    </Style.SheetListButtonFrame>


                </Style.SheetListFrame>

                </Modal> 

                </>
            )}
        </MediaQuery>
    </>
    );
};

export default AreaSiteGroupSelector;
